import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMeContext } from '../api/me-provider.js';
import { usePeople } from '../api/use-people.js';
import { PersonRow, bornOn } from '../components/person-row.js';
import { ThemeToggle } from '../components/theme-toggle.js';
import { useAccessToken } from '../auth/use-access-token.js';
import { sendSecondParentAgain, useParents, type Parents } from '../api/second-parents.js';
import { day } from '../components/video-format.js';
import { OwnSignatureRow } from '../components/own-signature-row.js';
import { WaitNote } from '../components/wait-note.js';
import { BusyLabel } from '../components/busy-label.js';
import { MyCredits } from '../components/my-credits.js';

/**
 * Account — your own card, then the people you are responsible for.
 *
 * The account holder appears ONCE, as the card at the top, and is not repeated
 * in the list below it. The card is a control rather than a heading: it opens
 * the same page a child's row opens, because an adult is a person in the system
 * like any other — they sign a waiver too, and if they train they have lessons.
 *
 * *Add a child* is the last row of the athletes list, not a button at the
 * bottom of the screen. The action belongs to that list.
 *
 * Notifications and payments are NOT here yet. The mockup draws them because
 * the page's shape was the decision, but a row that goes nowhere is a trap, so
 * each one arrives when it is built.
 *
 * THE OTHER PARENT (canvas row D, approved 17 October) sits between the card
 * and the athletes. The first parent sees Invite another parent, then the
 * invited row with Send again, then the joined row, which opens their page.
 * The second parent's card is their own, the first parent is the other
 * parent, and Add a child is not offered (first parent only, ruled 17
 * October).
 *
 * NO COACHING ROWS. Availability and Today's lessons sat here, between the
 * card and the athletes, until 11 October 2026, when they moved to Home
 * (components/coaching-rows.tsx).
 */
export function AccountRoute() {
  const { state: meState } = useMeContext();
  const { state, reload } = usePeople();
  const { state: parentsState, reload: reloadParents } = useParents();
  const parents = parentsState.status === 'ready' ? parentsState.parents : null;
  const second = meState.status === 'ready' && meState.me.profile?.role === 'second';
  const myProfile = meState.status === 'ready' ? meState.me.profile : undefined;

  const email = meState.status === 'ready' ? meState.me.email : null;
  const holder = state.status === 'ready' ? state.people.find((p) => p.is_account_holder) : undefined;
  const athletes = state.status === 'ready' ? state.people.filter((p) => !p.is_account_holder) : [];

  return (
    <>
      {state.status === 'loading' ? (
        <section className="panel">
          <WaitNote className="empty">Checking…</WaitNote>
        </section>
      ) : null}

      {state.status === 'failed' ? (
        <section className="panel">
          <p className="empty">{state.message}</p>
          <button type="button" onClick={reload}>
            Try again
          </button>
        </section>
      ) : null}

      {second && myProfile ? (
        <div className="person-row lead">
          <span className="person-row-text">
            <span className="name">
              {myProfile.first_name} {myProfile.last_name}
            </span>
            <span className="detail">{[email, countryName(myProfile.country)].filter(Boolean).join(' · ')}</span>
          </span>
        </div>
      ) : null}

      {holder && !second ? (
        <PersonRow
          clientId={holder.client_id}
          name={`${holder.first_name} ${holder.last_name}`}
          detail={[email, countryName(holder.country)].filter(Boolean).join(' · ')}
          lead
        />
      ) : null}

      {/* Your own Performance Signature, under your card (25 October). */}
      {meState.status === 'ready' && meState.me.profile ? <OwnSignatureRow coach={meState.me.is_mpc_coach} /> : null}

      {parents ? <OtherParent parents={parents} onSent={reloadParents} /> : null}

      {state.status === 'ready' ? (
        <>
          <span className="section-label">Athletes</span>

          {athletes.map((person) => (
            <PersonRow
              key={person.client_id}
              clientId={person.client_id}
              name={`${person.first_name} ${person.last_name}`}
              detail={bornOn(person.date_of_birth)}
            />
          ))}

          {second ? null : (
            <Link className="add-row" to="/account/people/new">
              <span aria-hidden="true">+</span> Add a child
            </Link>
          )}
        </>
      ) : null}

      {/* Remaining credits (031; board C5): every parent of a family sees it. */}
      {athletes.length > 0 ? <MyCredits /> : null}

      <span className="section-label">Theme</span>
      <ThemeToggle />
    </>
  );
}

/**
 * "Other parent" (canvas row D). For the first parent: Invite another parent;
 * the invited row, tagged Invited with when it was sent, and Send again; or
 * the joined row with when they joined. Both rows open the other parent's
 * page. For the second parent: the first parent, to read.
 */
function OtherParent({ parents, onSent }: { parents: Parents; onSent: () => void }) {
  const getToken = useAccessToken();
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const { holder, other } = parents;

  async function again(id: string) {
    if (sending) return;
    setSending(true);
    setFailed(false);
    try {
      await sendSecondParentAgain(getToken, id);
      onSent();
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  }

  if (parents.you === 'second') {
    return (
      <>
        <span className="section-label">Other adult</span>
        <div className="person-row">
          <span className="person-row-text">
            <span className="name">
              {holder.first_name} {holder.last_name}
            </span>
            {holder.email ? <span className="detail">{holder.email}</span> : null}
          </span>
        </div>
      </>
    );
  }

  return (
    <>
      <span className="section-label">Other adult</span>
      {other === null ? (
        <Link className="add-row" to="/account/other-adult/new">
          <span aria-hidden="true">+</span> Invite another adult
        </Link>
      ) : (
        <Link className="person-row" to="/account/other-adult">
          <span className="person-row-text">
            <span className="name">
              {other.first_name} {other.last_name}
            </span>
            <span className="detail">
              {[
                other.email,
                other.joined_at ? `Joined ${day(other.joined_at)}` : other.sent_at ? `Sent ${day(other.sent_at)}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
          {other.joined_at ? null : <span className="row-tag">Invited</span>}
          <span className="chevron" aria-hidden="true">
            ›
          </span>
        </Link>
      )}
      {other && !other.joined_at ? (
        <button type="button" className="secondary" onClick={() => void again(other.id)} disabled={sending} aria-busy={sending}>
          <BusyLabel busy={sending} working="Sending…">
            Send again
          </BusyLabel>
        </button>
      ) : null}
      {failed ? (
        <p className="form-note" role="alert">
          That did not work. Try again.
        </p>
      ) : null}
    </>
  );
}

/**
 * The two countries an account can be in, spelled out.
 *
 * Deliberately small and local. A full ISO 3166 table would be a list of
 * countries the system cannot serve, and a customer sees only what exists for
 * the country on their account. A code this does not know is shown as itself.
 */
function countryName(code: string | null): string | null {
  if (code === 'US') return 'United States';
  if (code === 'CA') return 'Canada';
  return code;
}
